import { UserDTO } from "@dto/usersResponse.dto";
import { SOCKET_IO_PATH } from "@dto/websocketMessage.dto";
import { ValidatorRule } from '@rc-component/form/es/interface';
import { App } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { Location } from "react-router";
import { AppState } from "src/App";
import { useAppGlobStore } from "src/appGlobalStore";
import { AUTOPLAY_INTERVAL_FALLBACK_S } from "src/boxParts/dashboard/autoplay/autoplayConst";
import { Label } from "src/data/Label";
import { DateUtil } from "../common/util/DateUtil";
import { AllType, RoutePath } from '../data/Routes';
import { MenuNode } from '../model/MenuNode';
import { Auth } from "../service/auth.service";
import { MenuService } from "../service/menu.service";
import { Deferred } from "./Deferred";

type ReturnTypePromise<T> = T extends (...args: unknown[]) => Promise<infer R> ? R : unknown;

const PATH_NO_DEVICE = RoutePath.SETTINGS + RoutePath.CONFIGURATION + RoutePath.STORAGE;
const PATH_DEFAULT_DASHBOARD = RoutePath.DASHBOARD + RoutePath.DEFAULT;

/**
 * Built once and reused by naturalSort. `String.localeCompare(b, undefined, options)` has to
 * resolve collation from those options on every single call, which a sort does O(n log n)
 * times - a reused Intl.Collator is the same comparison without that per-call setup.
 */
const NATURAL_COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

declare const publicUrl: string;

/**
 * Static utility class
 */
export class GLOB {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() { }

  /**
   * Checks PROD / DEV UI build
   * @returns true if running production build
   */
  static isProdBuild() {
    return import.meta.env.PROD;
  }

  /**
   * Checks PROD / DEV environment set on BE; fails closed, an unknown environment counts as production
   * @returns false only when the BE explicitly reports NODE_ENV other than production,
   * unless forced by VITE_DUMMY_BE_PROD=true (set it in .env.development.local to test production behavior against a dev backend)
   */
  static isProdEnv(): boolean {
    return useAppGlobStore.getState().info.backend?.production !== false
      || import.meta.env.VITE_DUMMY_BE_PROD === 'true';
  }

  /**
   * Get context URL path
   */
  static getPublicUrl(): string {
    return GLOB.isProdBuild() && publicUrl || import.meta.env.VITE_PUBLIC_URL;
  }

  /**
   *  URL part preceding app PATH
   */
  static readonly SERVER_URL = (__DEV_PROXY__ ? '' : import.meta.env.VITE_SERVER)
    + (import.meta.env.PROD && GLOB.getPublicUrl() || '');

  /**
   * URL for WebSocket, only ever consumed through SOCKET_IO below
   */
  private static readonly SERVER_WS_URL = ((__DEV_PROXY__ ? '' : import.meta.env.VITE_SERVER_WS) ||
    ((window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host)) +
    (import.meta.env.PROD && GLOB.getPublicUrl() || '');

  /**
   * Socket.IO target split as socket.io-client wants it: a URL path would be read as the
   * namespace, so the public-URL prefix has to travel in `path` instead.
   */
  static readonly SOCKET_IO = (() => {
    const { origin, pathname } = new URL(GLOB.SERVER_WS_URL);
    return { url: origin, path: pathname.replace(/\/$/, '') + SOCKET_IO_PATH };
  })();

  /**
   * Reference to Auth Service
   */
  static readonly authService = new Auth();

  /**
   * Reference for Menu Service
   */
  static readonly menuService = new MenuService();

  /**
   * Resolved when left tree menu is loaded
   */
  static readonly treeLoaded = new Deferred();

  /**
   * Resolved when backend info is obtained
   */
  static readonly infoLoaded = new Deferred();

  /**
   * User is logged in and required requests are resolved
   */
  static readonly xormonReady = new Deferred();

  /**
   * Load only graph items upto this limit
   */
  static graphTopItems = 20;

  /**
   * Hide or compact deleted items in graph
   */
  static showDeletedItems = false;

  /**
   * Optional label prepended to the browser tab title to distinguish multiple instances
   */
  static instanceLabel = '';

  /** Default seconds a dashboard stays on screen during autoplay. */
  static dashboardAutoplayInterval = AUTOPLAY_INTERVAL_FALLBACK_S;

  static readonly SHOW_RELEASE_INFO_KEY = 'showReleaseInfo';

  /**
   * Decimal separator
   */

  static decimalSeparator = '.';

  /**
   * CSV separator
   */
  static csvSeparator = ';';

  /**
   * Show what's new dialog after login
   */
  static showReleaseInfo = !!sessionStorage.getItem(GLOB.SHOW_RELEASE_INFO_KEY);

  /**
   * Application upgrade indicator
   */
  static isUpgrading = false;

  /**
   * True if Grafana configured
   */
  static grafanaActive: boolean;

  private static _userInfo: UserDTO;
  /**
   * Get currently logged-in user information
   */
  static get userInfo(): Readonly<UserDTO> { return GLOB._userInfo; }
  static set userInfo(value: UserDTO) {
    GLOB._userInfo = value;
    DateUtil.setOffset(value.timezone);
  }

  //#region routing

  /**
   * Router location
   */
  static location: Location;

  /**
   * AbortController for navigation
   */
  static navAborter: AbortController;

  /**
   * Class path e.g.: /storage
   */
  static selectedClass: RoutePath;// string;
  /**
   * All type path </type, /group, /device>
   */
  static selectedAllType: AllType;
  /**
   * itemId of the selected storage group. Matches MenuNode.getGroupPathSegment().
   */
  static selectedGroupId: string;
  /**
   * HW type path when allType=<null|type> e.g. /ISILON
   * or device path when allType=<group|device> e.g. /Isilon200
   */
  static selectedType: string;

  private static _selectedItem: MenuNode;
  /**
   * Left menu tree node according to last part of current route
   */
  static get selectedItem(): MenuNode {
    return this._selectedItem;
  }
  static set selectedItem(item: MenuNode) {
    this._selectedItem = item;
    setTimeout(() => {
      if (!useAppGlobStore.getState().leftMenuSelectedKeys || !useAppGlobStore.getState().leftMenuExpandedKeys) return;
      if (item) {
        useAppGlobStore.setState(prev => ({
          leftMenuSelectedKeys: [item.key],
          leftMenuExpandedKeys: [...prev.leftMenuExpandedKeys,
          ...MenuNode.getParentKeys(item.isLeaf ? item.parent : item)].filter(GLOB.uniqueArrayFilter)
        }));
      }
      else {
        useAppGlobStore.setState({ leftMenuSelectedKeys: [], leftMenuExpandedKeys: [] });
      }
      GLOB.updateTitle();
    });
  }

  static updateTitle() {
    const appName = GLOB.instanceLabel || Label.XORMON_NG;
    const item = GLOB._selectedItem;
    if (item) {
      document.title = appName + ' - ' + MenuNode.getParentNodes(item).map(mn => mn.titleText || mn.title).join(' | ');
    } else {
      document.title = appName;
    }
  }

  static getGroup(node: MenuNode = GLOB.selectedItem): MenuNode {
    if (!node?.parent) return;
    if (node.parent?.path === RoutePath.GROUP) return node;
    return GLOB.getGroup(node.parent);
  }

  //#endregion

  //#region state

  /**
   * Scroll left menu tree to show selected item
   */
  static scrollSelectedItemIntoView: () => void;

  static getAppState: () => Readonly<AppState>;
  static setAppState: <K extends keyof AppState>(
    state:
      | ((prevState: Readonly<AppState>, props: Readonly<unknown>) => Pick<AppState, K> | AppState | null)
      | (Pick<AppState, K> | AppState | null),
    callback?: () => void
  ) => void;

  /**
   * Rerender app
   */
  static refresh() {
    if (GLOB.getAppState && GLOB.setAppState)
      GLOB.setAppState(prev => ({ refresh: !prev.refresh }));
  }

  /**
   * AntD app context hooks
   */
  static app: ReturnType<typeof App.useApp>;

  //#endregion

  //#region functions

  /**
   * Checks if certificate will expire within 30 days
   * @returns true if certificate expires soon
   */
  static isCertificateEnding() {
    const validTo = useAppGlobStore.getState().info?.backend?.cert?.validTo;
    return dayjs(validTo).diff(undefined, 'day') < 30;
  }

  /**
   * Checks if enterprise edition license is valid
   * @returns true if active enterprise license
   */
  static isValidEnterprise() {
    const backend = useAppGlobStore.getState().info.backend;
    return backend.premium.valid && !backend.free;
  }

  /**
   * Checks if license is free or expired trial
   * @returns true if free, false for valid or expired enterprise
   */
  static isFreeOrExpiredTrial() {
    const backend = useAppGlobStore.getState().info.backend;
    return backend.free && !backend.premium?.expirationDate;
  }

  private static getActiveMenuClasses() {
    return useAppGlobStore.getState().topMenu?.filter(mn => mn.notActive === false);
  }
  /**
   * Get default route path
   * @returns
   */
  static getDefaultPath() {
    return GLOB.getActiveMenuClasses().length || !GLOB.userInfo.isAdmin ? PATH_DEFAULT_DASHBOARD : PATH_NO_DEVICE;
  }

  /**
   * Get last path of URL
   * @param url
   * @returns
   */
  static getItemPath(item: { url?: string; group_id?: number; itemId?: string; }) {
    if (GLOB.selectedClass === RoutePath.DASHBOARD && item.itemId) {
      return '/' + item.itemId;
    }
    else if (item.url) {
      return item.url.substring(item.url.lastIndexOf('/'));
    } else if (item.group_id)
      return '/' + item.group_id;
  }

  /**
   * Returns promise, which is resolved after timeout with result of callback
   * @param callback
   * @param timeout
   * @returns
   */
  static timeoutPromise<T = void>(callback: () => T, timeout?: number) {
    return new Promise<T>(resolve => setTimeout(() => {
      resolve(callback ? callback() : null);
    }, timeout));
  }

  /**
   * Executes callback after specified number of extra timeout cycles
   * @param callback function to call
   * @param count number of extra timeout cycles, executes callback in first timeout when count <= 0 (same as setTimeout), default=1
   */
  static multiTimeout(callback: () => void, count = 1) {
    window.setTimeout(() => {
      if (count > 0)
        GLOB.multiTimeout(callback, count - 1);
      else
        callback();
    });
  }

  /**
   * Async debounce wrapper. Lodash debounce doesn't handle async functions.
   */
  static debounceAsync<T extends (...args: Parameters<T>) => Promise<ReturnTypePromise<T>>>(func: T, wait: number): (...args: Parameters<T>) => Promise<ReturnTypePromise<T>> {
    let timerID = -1;

    return (...args) => {
      window.clearTimeout(timerID);

      const promiseForFunc = new Promise((resolve) => {
        timerID = window.setTimeout(resolve, wait);
      });

      return promiseForFunc.then(() => func(...args));
    };
  }

  /**
   * Removes null values
   * @param value
   * @param index
   * @param array
   * @returns
   */
  static uniqueArrayFilter(value, index: number, array: unknown[]) {
    return value && array.indexOf(value) === index;
  }

  /**
   * Keeps null values
   * @param value
   * @param index
   * @param array
   * @returns
   */
  static uniqueArrayFilterNull(value, index: number, array: unknown[]) {
    return array.indexOf(value) === index;
  }

  /**
   * Sorter function, case insensitive, numeric collation '2<10'
   * @param a
   * @param b
   * @returns negative value if a before b, 0 if same, positive value if a after b
   */
  static naturalSort(a, b) {
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }
    const as = typeof a === 'string' ? a : String(a);
    const bs = typeof b === 'string' ? b : String(b);
    return NATURAL_COLLATOR.compare(as, bs);
  }

  /**
   * Returns Boolean after check Regex pattern
   * @param value text to check
   * @returns true if input valid
   */
  private static isValidTitle(value: string) {
    // Any script's letters, digits and marks; the cap keeps out emoji, homoglyphs and invisible spaces.
    const regex = /^(?![\s\S]*[^\u{0020}-\u{FDFF}])[\p{L}\p{Mn}\p{Mc}\p{N}\u{0020}_&@,.:\-]+$/u;
    return regex.test(value);
  }
  static readonly TitleCharactersValidatorRule: ValidatorRule = {
    validator(rule, value: string, callback) {
      if (GLOB.isValidTitle(value)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Use only letters, numbers, and _ - & @ , ."));
    }
  };

  static readonly URL_PATTERN = /^https?:\/\/.+/i;

  /**
   * True unless the URL uses a script-injection scheme (javascript:, data:, vbscript:).
   * Browsers strip tab/newline and leading control chars when navigating, so they are removed before the scheme check.
   */
  static isSafeUrl(url: string) {
    return !/^(javascript|data|vbscript):/i.test(url?.replace(/[\u0000-\u0020]/g, '') ?? '');
  }

  static normalizeLinkUrl(url: string): string {
    if (/^https?:\/\//i.test(url) || url.startsWith('//'))
      return url;

    return `https://${url}`;
  }

  private static readonly LINK_URL_SCHEME = /^([a-z][a-z\d+.-]*):([\s\S]*)$/i;
  private static readonly LINK_URL_PORT = /^\d+(?:[/?#]|$)/;
  private static readonly LINK_URL_NUMERIC_SCHEMES = ['javascript', 'vbscript', 'data', 'tel', 'sms', 'fax', 'callto'];

  /**
   * True for an http(s) link, with or without scheme – abc, abc:8080/x is valid, it may resolve via search domain / hosts.
   * Render the link through {@link GLOB.normalizeLinkUrl}.
   */
  static isValidLinkUrl(url: string) {
    if (url.includes('\\'))
      return false;

    const [, scheme, rest] = GLOB.LINK_URL_SCHEME.exec(url) ?? [];
    if (scheme) {
      const lower = scheme.toLowerCase();
      if (lower === 'http' || lower === 'https') {
        if (!rest.startsWith('//'))
          return false;
      } else if (!GLOB.LINK_URL_PORT.test(rest) || GLOB.LINK_URL_NUMERIC_SCHEMES.includes(lower))
        return false;
    }

    try {
      const { protocol, hostname } = new URL(GLOB.normalizeLinkUrl(url), window.location.origin);
      return (protocol === 'http:' || protocol === 'https:') && /[^.]/.test(hostname);
    } catch {
      return false;
    }
  }

  //#endregion
}

axios.defaults.baseURL = GLOB.SERVER_URL;
axios.defaults.withCredentials = true;
